/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { environmentsClone } from "../funcs/environmentsClone.js";
import { environmentsCreate } from "../funcs/environmentsCreate.js";
import { environmentsDeleteBySlug } from "../funcs/environmentsDeleteBySlug.js";
import { environmentsDeleteSourceLink } from "../funcs/environmentsDeleteSourceLink.js";
import { environmentsDeleteToolsetLink } from "../funcs/environmentsDeleteToolsetLink.js";
import { environmentsGetBySource } from "../funcs/environmentsGetBySource.js";
import { environmentsGetByToolset } from "../funcs/environmentsGetByToolset.js";
import { environmentsList } from "../funcs/environmentsList.js";
import { environmentsSetSourceLink } from "../funcs/environmentsSetSourceLink.js";
import { environmentsSetToolsetLink } from "../funcs/environmentsSetToolsetLink.js";
import { environmentsUpdateBySlug } from "../funcs/environmentsUpdateBySlug.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { Environment } from "../models/components/environment.js";
import { ListEnvironmentsResult } from "../models/components/listenvironmentsresult.js";
import { SourceEnvironmentLink } from "../models/components/sourceenvironmentlink.js";
import { ToolsetEnvironmentLink } from "../models/components/toolsetenvironmentlink.js";
import {
  CloneEnvironmentRequest,
  CloneEnvironmentSecurity,
} from "../models/operations/cloneenvironment.js";
import {
  CreateEnvironmentRequest,
  CreateEnvironmentSecurity,
} from "../models/operations/createenvironment.js";
import {
  DeleteEnvironmentRequest,
  DeleteEnvironmentSecurity,
} from "../models/operations/deleteenvironment.js";
import {
  DeleteSourceEnvironmentLinkRequest,
  DeleteSourceEnvironmentLinkSecurity,
} from "../models/operations/deletesourceenvironmentlink.js";
import {
  DeleteToolsetEnvironmentLinkRequest,
  DeleteToolsetEnvironmentLinkSecurity,
} from "../models/operations/deletetoolsetenvironmentlink.js";
import {
  GetSourceEnvironmentRequest,
  GetSourceEnvironmentSecurity,
} from "../models/operations/getsourceenvironment.js";
import {
  GetToolsetEnvironmentRequest,
  GetToolsetEnvironmentSecurity,
} from "../models/operations/gettoolsetenvironment.js";
import {
  ListEnvironmentsRequest,
  ListEnvironmentsSecurity,
} from "../models/operations/listenvironments.js";
import {
  SetSourceEnvironmentLinkRequest,
  SetSourceEnvironmentLinkSecurity,
} from "../models/operations/setsourceenvironmentlink.js";
import {
  SetToolsetEnvironmentLinkRequest,
  SetToolsetEnvironmentLinkSecurity,
} from "../models/operations/settoolsetenvironmentlink.js";
import {
  UpdateEnvironmentRequest,
  UpdateEnvironmentSecurity,
} from "../models/operations/updateenvironment.js";
import { unwrapAsync } from "../types/fp.js";

export class Environments extends ClientSDK {
  /**
   * cloneEnvironment environments
   *
   * @remarks
   * Clone an environment into a new one. Either copies only the variable names with empty placeholder values, or copies the encrypted values verbatim. Encrypted secret values are never decrypted by the application during the clone operation.
   */
  async clone(
    request: CloneEnvironmentRequest,
    security?: CloneEnvironmentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Environment> {
    return unwrapAsync(environmentsClone(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createEnvironment environments
   *
   * @remarks
   * Create a new environment
   */
  async create(
    request: CreateEnvironmentRequest,
    security?: CreateEnvironmentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Environment> {
    return unwrapAsync(environmentsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteEnvironment environments
   *
   * @remarks
   * Delete an environment
   */
  async deleteBySlug(
    request: DeleteEnvironmentRequest,
    security?: DeleteEnvironmentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(environmentsDeleteBySlug(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteSourceEnvironmentLink environments
   *
   * @remarks
   * Delete a link between a source and an environment
   */
  async deleteSourceLink(
    request: DeleteSourceEnvironmentLinkRequest,
    security?: DeleteSourceEnvironmentLinkSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(environmentsDeleteSourceLink(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteToolsetEnvironmentLink environments
   *
   * @remarks
   * Delete a link between a toolset and an environment
   */
  async deleteToolsetLink(
    request: DeleteToolsetEnvironmentLinkRequest,
    security?: DeleteToolsetEnvironmentLinkSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(environmentsDeleteToolsetLink(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getSourceEnvironment environments
   *
   * @remarks
   * Get the environment linked to a source
   */
  async getBySource(
    request: GetSourceEnvironmentRequest,
    security?: GetSourceEnvironmentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Environment> {
    return unwrapAsync(environmentsGetBySource(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getToolsetEnvironment environments
   *
   * @remarks
   * Get the environment linked to a toolset
   */
  async getByToolset(
    request: GetToolsetEnvironmentRequest,
    security?: GetToolsetEnvironmentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Environment> {
    return unwrapAsync(environmentsGetByToolset(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listEnvironments environments
   *
   * @remarks
   * List all environments for an organization
   */
  async list(
    request?: ListEnvironmentsRequest | undefined,
    security?: ListEnvironmentsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListEnvironmentsResult> {
    return unwrapAsync(environmentsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setSourceEnvironmentLink environments
   *
   * @remarks
   * Set (upsert) a link between a source and an environment
   */
  async setSourceLink(
    request: SetSourceEnvironmentLinkRequest,
    security?: SetSourceEnvironmentLinkSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SourceEnvironmentLink> {
    return unwrapAsync(environmentsSetSourceLink(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * setToolsetEnvironmentLink environments
   *
   * @remarks
   * Set (upsert) a link between a toolset and an environment
   */
  async setToolsetLink(
    request: SetToolsetEnvironmentLinkRequest,
    security?: SetToolsetEnvironmentLinkSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ToolsetEnvironmentLink> {
    return unwrapAsync(environmentsSetToolsetLink(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateEnvironment environments
   *
   * @remarks
   * Update an environment
   */
  async updateBySlug(
    request: UpdateEnvironmentRequest,
    security?: UpdateEnvironmentSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Environment> {
    return unwrapAsync(environmentsUpdateBySlug(
      this,
      request,
      security,
      options,
    ));
  }
}
